import { useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  Tooltip,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import { createFilterOptions } from "@mui/material/Autocomplete";

const filterByName = createFilterOptions();
const filterByTenantId = createFilterOptions({
  stringify: (tenant) => tenant.id ?? "",
  trim: true,
});

/**
 * Shared tenant selection Autocomplete with:
 * - Alphabetical sorting by name
 * - Search by tenant ID for queries starting with "tnt_", otherwise by name
 * - Built-in "Hide all demo accounts" filter icon inside the input
 *
 * Props:
 * - tenants: Array of tenant objects (must have `id`, `name`, optionally `demoAccount`)
 * - value: Currently selected tenant object (or null)
 * - onChange: (event, newValue) => void
 * - loading: boolean (shows spinner in input)
 * - label: string (rendered as input label)
 * - placeholder: string
 * - noOptionsText: string
 * - size: "small" | "medium"
 * - fullWidth: boolean
 * - sx: object (applied to root Autocomplete)
 * - inputSx: object (applied to the TextField)
 * - ...rest: forwarded to MUI Autocomplete
 */
const TenantAutocomplete = ({
  tenants = [],
  value = null,
  onChange,
  loading = false,
  label,
  placeholder = "Choose a tenant",
  noOptionsText,
  size,
  fullWidth,
  sx,
  inputSx,
  ...rest
}) => {
  const [open, setOpen] = useState(false);
  const [hideDemoAccounts, setHideDemoAccounts] = useState(true);

  const options = useMemo(() => {
    let list = [...tenants];
    if (hideDemoAccounts) {
      list = list.filter((t) => {
        const val = t.demoAccount ?? t.DemoAccount;
        const hasDemoName = /^(ZT|ZZ|Z CHURNED)/i.test((t.name ?? "").trimStart());
        return val !== true && String(val).toLowerCase() !== "true" && !hasDemoName;
      });
    }
    return list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [tenants, hideDemoAccounts]);

  const handleFilterToggle = (e) => {
    e.stopPropagation();
    setHideDemoAccounts((prev) => !prev);
  };

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      filterOptions={(options, state) =>
        state.inputValue.trim().startsWith("tnt_")
          ? filterByTenantId(options, state)
          : filterByName(options, state)
      }
      value={value}
      getOptionLabel={(option) => option.name ?? option.id ?? ""}
      isOptionEqualToValue={(option, v) => option.id === v.id}
      onChange={(event, newValue, reason) => {
        setOpen(false);
        if (onChange) onChange(event, newValue, reason);
      }}
      loading={loading}
      noOptionsText={
        noOptionsText ?? (loading ? "Loading tenants…" : "No tenants available")
      }
      size={size}
      fullWidth={fullWidth}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          variant="outlined"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress size={18} sx={{ color: "#f97316" }} />}
                <Tooltip title={hideDemoAccounts ? "Show all demo accounts" : "Hide all demo accounts"}>
                  <Box
                    component="span"
                    onClick={handleFilterToggle}
                    onMouseDown={(e) => e.preventDefault()}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      cursor: "pointer",
                      borderRadius: "6px",
                      p: 0.5,
                      transition: "all 0.15s ease",
                      backgroundColor: hideDemoAccounts ? "rgba(249, 115, 22, 0.1)" : "transparent",
                      "&:hover": {
                        backgroundColor: hideDemoAccounts
                          ? "rgba(249, 115, 22, 0.18)"
                          : "rgba(15, 23, 42, 0.06)",
                      },
                    }}
                  >
                    <FilterListIcon
                      sx={{
                        fontSize: 20,
                        color: hideDemoAccounts ? "#f97316" : "#9ca3af",
                      }}
                    />
                  </Box>
                </Tooltip>
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          sx={
            inputSx || {
              "& .MuiOutlinedInput-root": {
                minHeight: 56,
                borderRadius: "16px",
                backgroundColor: "#fff",
                fontWeight: 600,
                color: "#202531",
                "& fieldset": { borderColor: "rgba(15, 23, 42, 0.08)" },
                "&:hover fieldset": { borderColor: "rgba(249, 115, 22, 0.35)" },
                "&.Mui-focused fieldset": {
                  borderColor: "#fb923c",
                  borderWidth: "1px",
                },
              },
            }
          }
        />
      )}
      sx={sx}
      {...rest}
    />
  );
};

export default TenantAutocomplete;
